import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  RANGEMENT_OBJECTIVE_STATE_LABEL,
  type RowExtract,
  rangementObjectiveTone,
  rangementTargetOf,
  scribeReading,
  turnPlace,
} from '@/lib/session-rangement';
import { cn } from '@/lib/utils';
import type { RangementObjective, SessionDetail } from '@matter_extractor/shared';

/**
 * The table of the block « Rangement de la matière » (scenario-succes.md §7.1;
 * maquette `Main`): one row per target, in three columns.
 *
 * - the OBJECTIVE and its step - « 1 · Déploiement », the rank of the objective
 *   in its step, then the step;
 * - « Scribe, pendant l'entretien »: the status and the LIVING extracts of the
 *   last snapshot, each with its turn - what drove the interviewer;
 * - « Rangeur, lu par l'analyste »: the ranger's column, tinted so that the
 *   eye finds the one the analyst read. The state, what is missing, the
 *   formula - marked « Formule du rangeur, une reformulation », the only
 *   sentence in the block that is not the person's - and the extracts with
 *   their turn and their step.
 *
 * A row the figure has no objective for draws « - » in the ranger's column: a
 * session with no rangement, one that failed, one that is still running.
 *
 * ## The same table for a TRIAL
 *
 * A trial fills the ranger's column and says so on its head: « à blanc, v13 ».
 * The columns of the scribe do not move, which is what lets the two be read
 * side by side.
 */

export interface RangementTableProps {
  readonly detail: SessionDetail;
  /** The objectives to draw, in order. */
  readonly ids: readonly string[];
  /** What the ranger rendered; null draws « - » on every row. */
  readonly objectives: readonly RangementObjective[] | null;
  /** The version of the development consigne when the column is a TRIAL's, null on the session's own. */
  readonly trialVersion?: number | null;
}

const HEAD_CELL =
  'px-3.5 py-2.5 text-left font-semibold text-gray-600 text-xs uppercase leading-4 tracking-wide';

export function RangementTable({
  detail,
  ids,
  objectives,
  trialVersion = null,
}: RangementTableProps) {
  return (
    // Scrolls sideways on a narrow screen rather than squeezing three columns of quotes.
    <div className="overflow-x-auto rounded-md border border-gray-100">
      <table
        data-testid={SESSIONS_TEST_IDS.walkRangementTable}
        className="w-full min-w-[640px] table-fixed border-collapse"
      >
        <caption className="sr-only">Matière par objectif, scribe et rangeur</caption>
        <colgroup>
          <col className="w-[200px]" />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr className="border-gray-200 border-b bg-white">
            <th scope="col" className={HEAD_CELL}>
              Objectif
            </th>
            <th scope="col" className={cn(HEAD_CELL, 'border-gray-100 border-l')}>
              Scribe, pendant l’entretien
            </th>
            <th
              scope="col"
              className={cn(HEAD_CELL, 'border-gray-100 border-l bg-bleu-nuit-tint text-bleu-nuit')}
            >
              {trialVersion === null ? (
                'Rangeur, lu par l’analyste'
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  Rangeur
                  <span className="rounded-full bg-white px-2 py-px font-semibold text-bleu-nuit text-xs normal-case tracking-normal">
                    à blanc, v{trialVersion}
                  </span>
                </span>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {ids.map((id) => {
            const target = rangementTargetOf(detail, id);
            const scribe = scribeReading(detail, id);
            const ranged = objectives?.find((objective) => objective.objectiveId === id) ?? null;
            return (
              <tr
                key={id}
                data-testid={SESSIONS_TEST_IDS.walkRangementRow}
                data-objective={id}
                className="border-gray-100 border-b align-top last:border-b-0"
              >
                <th scope="row" className="px-3.5 py-3 text-left font-normal">
                  <span className="flex flex-col gap-0.5">
                    <span className="font-semibold text-gray-900 text-sm">{target.name}</span>
                    {target.stepName === null ? null : (
                      <span className="text-gray-600 text-xs">
                        {target.rank} · {target.stepName}
                      </span>
                    )}
                  </span>
                </th>

                <td
                  data-testid={SESSIONS_TEST_IDS.walkRangementScribeCell}
                  className="border-gray-100 border-l px-3.5 py-3"
                >
                  <div className="flex flex-col gap-2">
                    <span className="self-start">
                      <StatusBadge tone={scribe.tone}>{scribe.label}</StatusBadge>
                    </span>
                    <ExtractList
                      extracts={scribe.extracts}
                      where={(extract) => `t${extract.turn}`}
                    />
                  </div>
                </td>

                <td
                  data-testid={SESSIONS_TEST_IDS.walkRangementRangerCell}
                  data-state={ranged?.state ?? 'none'}
                  className="border-gray-100 border-l bg-bleu-nuit-tint/30 px-3.5 py-3"
                >
                  <div className="flex flex-col gap-2">
                    {ranged === null ? (
                      <span className="text-gray-600 text-sm">-</span>
                    ) : (
                      <RangerCell detail={detail} ranged={ranged} ghost={target.ghost} />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The ranger's cell: state, what is missing, the formula and its extracts. */
function RangerCell({
  detail,
  ranged,
  ghost = false,
}: {
  detail: SessionDetail;
  ranged: RangementObjective;
  ghost?: boolean;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={rangementObjectiveTone(ranged.state)}>
          {RANGEMENT_OBJECTIVE_STATE_LABEL[ranged.state]}
        </StatusBadge>
        {ranged.missing === '' ? null : (
          <span className="text-jaune-deep text-xs leading-4">{ranged.missing}</span>
        )}
        {ghost ? (
          <span className="text-gray-600 text-xs">
            objectif {ranged.objectiveId}, que la version jouée ne porte pas
          </span>
        ) : null}
      </div>
      {ranged.formula === '' ? null : (
        <>
          <p
            data-testid={SESSIONS_TEST_IDS.walkRangementFormula}
            className="text-bleu-nuit text-sm italic leading-5"
          >
            {ranged.formula}
          </p>
          <p className="text-gray-600 text-micro leading-3.5">
            Formule du rangeur, une reformulation
          </p>
        </>
      )}
      <ExtractList
        extracts={ranged.extracts}
        where={(extract) => turnPlace(detail, extract.turn)}
        ink="text-gray-900"
      />
    </>
  );
}

/** « t9 · Déploiement » then the words of the person, quoted. */
function ExtractList({
  extracts,
  where,
  ink = 'text-gray-700',
}: {
  extracts: readonly RowExtract[];
  where: (extract: RowExtract) => string;
  ink?: string;
}) {
  if (extracts.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1.5">
      {extracts.map((extract, index) => (
        <li
          // By position: an objective may hold one sentence twice, on two turns.
          key={`${extract.turn}-${index}`}
          className={cn('flex gap-2 text-caption leading-5', ink)}
        >
          <span className="w-fit max-w-[40%] shrink-0 text-gray-600 text-xs tabular-nums leading-5">
            {where(extract)}
          </span>
          <span>« {extract.text} »</span>
        </li>
      ))}
    </ul>
  );
}
